import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { INVOICE_STATUSES_ARRAY, InvoiceStatus, SettingsNotificationsDto } from '@sr/dto';
import { useEffect, useState } from 'react';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { useSnack } from 'shared/ui/snack';
import { settingsApi } from '../api';

type Editor = 'leadAssignment' | 'invoiceStatusChange' | null;

export const NotificationsForm = () => {
  const { data, isLoading, error } = settingsApi.useGetNotificationsSettingsQuery();
  const [update, { isLoading: isSaving }] = settingsApi.useSetNotificationsSettingsMutation();
  const [settings, setSettings] = useState<SettingsNotificationsDto | null>(null);
  const [editor, setEditor] = useState<Editor>(null);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftStatuses, setDraftStatuses] = useState<InvoiceStatus[]>([]);
  const { showError, showSuccess } = useSnack();

  useEffect(() => {
    if (data) setSettings(data);
  }, [data]);

  const save = async (next: SettingsNotificationsDto) => {
    try {
      const saved = await update(next).unwrap();
      setSettings(saved);
      showSuccess('Настройки уведомлений обновлены');
    } catch (e) {
      showError(`Ошибка обновления настроек: ${extractNestJsErrorMessage(e)}`);
    }
  };

  const toggle = (key: Exclude<Editor, null>, enabled: boolean) => {
    if (!settings) return;
    void save({ ...settings, [key]: { ...settings[key], enabled } });
  };

  const openEditor = (key: Exclude<Editor, null>) => {
    if (!settings) return;
    setEditor(key);
    setDraftSubject(settings[key].subject);
    setDraftBody(settings[key].body);
    setDraftStatuses(settings.invoiceStatusChange.statuses);
  };

  const saveEditor = () => {
    if (!settings || !editor || !draftSubject.trim() || !draftBody.trim()) return;
    const next: SettingsNotificationsDto = {
      ...settings,
      [editor]: {
        ...settings[editor],
        subject: draftSubject.trim(),
        body: draftBody.trim(),
        ...(editor === 'invoiceStatusChange' ? { statuses: draftStatuses } : {}),
      },
    };
    setEditor(null);
    void save(next);
  };

  const toggleStatus = (status: InvoiceStatus) => {
    setDraftStatuses((current) => current.includes(status) ? current.filter((item) => item !== status) : [...current, status]);
  };

  if (error) return <ErrorBanner errorMessage={extractNestJsErrorMessage(error)} />;
  if (isLoading || !settings) return <LoadingBanner />;

  return (
    <>
      <Card sx={{ maxWidth: 760 }}>
        <NotificationRow
          title="Назначение лида"
          description="Письмо менеджеру, когда на него назначен лид"
          enabled={settings.leadAssignment.enabled}
          disabled={isSaving}
          onToggle={(enabled) => toggle('leadAssignment', enabled)}
          onEdit={() => openEditor('leadAssignment')}
        />
        <NotificationRow
          title="Изменение статуса счета"
          description={settings.invoiceStatusChange.statuses.length
            ? `Письмо менеджеру при статусах: ${settings.invoiceStatusChange.statuses.map((status) => INVOICE_STATUSES_ARRAY.find(({ id }) => id === status)?.label).join(', ')}`
            : 'Статусы для уведомления не выбраны'}
          enabled={settings.invoiceStatusChange.enabled}
          disabled={isSaving}
          onToggle={(enabled) => toggle('invoiceStatusChange', enabled)}
          onEdit={() => openEditor('invoiceStatusChange')}
        />
      </Card>

      <Dialog open={editor !== null} onClose={() => setEditor(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editor === 'leadAssignment' ? 'Назначение лида' : 'Изменение статуса счета'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {editor === 'invoiceStatusChange' && (
              <Box>
                <Typography variant="subtitle2">Уведомлять при статусах</Typography>
                <Stack direction="row" flexWrap="wrap">
                  {INVOICE_STATUSES_ARRAY.map(({ id, label }) => (
                    <FormControlLabel
                      key={id}
                      control={<Checkbox checked={draftStatuses.includes(id as InvoiceStatus)} onChange={() => toggleStatus(id as InvoiceStatus)} />}
                      label={label}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            <TextField
              autoFocus
              label="Заголовок письма"
              value={draftSubject}
              onChange={(event) => setDraftSubject(event.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText={editor === 'leadAssignment'
                ? 'Переменные: {{leadId}}, {{leadSubject}}, {{managerName}}, {{leadUrl}}'
                : 'Переменные: {{invoiceId}}, {{invoiceNumber}}, {{invoiceStatus}}, {{managerName}}, {{invoiceUrl}}'}
            />
            <TextField
              label="Тело письма"
              value={draftBody}
              onChange={(event) => setDraftBody(event.target.value)}
              multiline
              minRows={5}
              inputProps={{ maxLength: 4096 }}
              helperText={editor === 'leadAssignment'
                ? 'Для ссылки используйте {{leadUrl}}'
                : 'Для ссылки используйте {{invoiceUrl}}'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)}>Отмена</Button>
          <Button variant="contained" onClick={saveEditor} disabled={!draftSubject.trim() || !draftBody.trim() || isSaving}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

type RowProps = {
  title: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
};

const NotificationRow = ({ title, description, enabled, disabled, onToggle, onEdit }: RowProps) => (
  <Box
    onClick={onEdit}
    sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:last-child': { borderBottom: 0 } }}
  >
    <Switch
      checked={enabled}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={(_, checked) => onToggle(checked)}
    />
    <Box flex={1}>
      <Typography fontWeight={500}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </Box>
    <IconButton aria-label={`Редактировать: ${title}`} onClick={(event) => { event.stopPropagation(); onEdit(); }}>
      <EditOutlinedIcon />
    </IconButton>
  </Box>
);
